import DOMPurify from 'dompurify';
const richTextPrefix = '<!--TINYMCE-->';
// Add a default hook to the sanitizer to add target and rel to all elements
// DOMPurify doesn't do this by default and will just use links without _blank https://github.com/cure53/DOMPurify/issues/317
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof HTMLAnchorElement)) {
        return;
    }
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
});
export function isNullOrUndefined(element) {
    return typeof element === 'undefined' || element === null;
}
export function isTrue(variable) {
    if (isNullOrUndefined(variable)) {
        return false;
    }
    return variable === true || variable === 'true';
}
export function isFunction(variable) {
    return typeof variable === 'function';
}
export function isObject(val) {
    return val !== null && typeof val === 'object';
}
export function isNumeric(val) {
    if (typeof val === 'number') {
        return true;
    }
    if (typeof val !== 'string') {
        return false;
    }
    if (val.trim() === '') {
        return false;
    }
    return !isNaN(Number(val)) && isFinite(Number(val));
}
export function normalizeNumericInput(input) {
    var _a, _b;
    if (typeof input === 'number') {
        return input;
    }
    if (typeof input !== 'string' || !isNumeric(input)) {
        return null;
    }
    // TODO use library instead of all the complicated logic below?!?!
    let normalizedInput = input.trim();
    // Handle cases where both ',' and '.' are present
    if (normalizedInput.includes(',') && normalizedInput.includes('.')) {
        const lastComma = normalizedInput.lastIndexOf(',');
        const lastDot = normalizedInput.lastIndexOf('.');
        if (lastDot > lastComma) {
            // '.' is the decimal, ',' is a thousand separator
            normalizedInput = normalizedInput.replace(/,/g, ''); // Remove the thousand separators
        }
        else {
            // ',' is the decimal, '.' is a thousand separator
            normalizedInput = normalizedInput.replace(/\./g, '').replace(',', '.'); // Remove thousand separators and replace ',' with '.'
        }
    }
    else if (normalizedInput.includes(',')) {
        // Handle cases where only ',' is present
        const parts = normalizedInput.split(',');
        if (parts.length > 1 && parts.slice(0, -1).every(part => part.length === 3) && ((_a = parts[parts.length - 1]) === null || _a === void 0 ? void 0 : _a.length) !== 2) {
            // ',' is a thousand separator
            normalizedInput = normalizedInput.replace(/,/g, ''); // Remove the thousand separators
        }
        else {
            // ',' is the decimal separator
            normalizedInput = normalizedInput.replace(',', '.'); // Replace ',' with '.'
        }
    }
    else if (normalizedInput.includes('.')) {
        // Handle cases where only '.' is present
        const parts = normalizedInput.split('.');
        if (parts.length > 1 && parts.slice(0, -1).every(part => part.length === 3) && ((_b = parts[parts.length - 1]) === null || _b === void 0 ? void 0 : _b.length) !== 2) {
            // '.' is a thousand separator
            normalizedInput = normalizedInput.replace(/\./g, ''); // Remove the thousand separators
        }
    }
    // Parse the normalized input to a float
    const numericValue = parseFloat(normalizedInput);
    if (isNaN(numericValue)) {
        return null;
    }
    return numericValue;
}
export function callIfFunction(callback, args = {}) {
    if (!isFunction(callback)) {
        return false;
    }
    return callback(args);
}
export function convertToBoolean(value) {
    return !!value;
}
export function richText(text) {
    return `<div class="rich-text">${sanitizeHtmlWithMath(text)}</div>`;
}
export function setRichTextPrefixWhenMissing(text) {
    if (text === null || hasRichTextPrefix(text)) {
        return text !== null && text !== void 0 ? text : '';
    }
    const trimmedText = text.trim();
    if (trimmedText === '<!---->') {
        return '';
    }
    if (hasRichTextPrefix(trimmedText)) {
        return text;
    }
    let result = internalNl2br(text);
    if (!result.startsWith('<p>') && !result.endsWith('</p>')) {
        result = `<p>${result}</p>`;
    }
    return `${richTextPrefix}${result}`;
}
export function getPlaintextFromHtml(html) {
    var _a, _b;
    const div = document.createElement('div');
    div.innerHTML = html;
    return (_b = (_a = div.textContent) !== null && _a !== void 0 ? _a : div.innerText) !== null && _b !== void 0 ? _b : '';
}
export function sanitizeHtmlWithMath(text) {
    if (!text) {
        return '';
    }
    return DOMPurify.sanitize(text, {
        ALLOWED_TAGS: ['br', 'strong', 'a', 'span', 'em', 'ol', 'ul', 'li', 'p', 'math-field'],
        ALLOWED_ATTR: ['contenteditable', 'style'],
        ADD_TAGS: ['#comment'],
        FORCE_BODY: true,
    });
}
export function hasRichTextPrefix(text) {
    if (text === null) {
        return false;
    }
    return text.startsWith(richTextPrefix);
}
export function potentiallyConvertToRichText(text, convertNewlines = false) {
    var _a;
    if (text === null) {
        return '';
    }
    (_a = window.HiHaHoPlayer) === null || _a === void 0 ? void 0 : _a.placeholders.replace(text, false);
    if (convertNewlines) {
        text = internalNl2br(text);
    }
    return hasRichTextPrefix(text)
        ? richText(text)
        : sanitizeHtmlWithMath(text).trim();
}
function internalNl2br(str) {
    if (typeof str !== 'string') {
        return '';
    }
    return str.replace(/(?:\\[rn]|[\r\n]+)+/g, '<br>');
}
export function nl2br(str, allowedTags = ['br']) {
    if (typeof str !== 'string') {
        return '';
    }
    return DOMPurify.sanitize(internalNl2br(str), { ALLOWED_TAGS: allowedTags });
}
export function isBoolTrue(value) {
    return value === '1' || value === 1 || value === 'true' || value === true;
}
export function isValidHttpUrl(urlString) {
    let url;
    try {
        url = new URL(urlString);
    }
    catch (e) {
        return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
}
export function ensureUrlHasHyperTextTransferProtocol(urlString) {
    urlString = urlString.trim();
    if (!urlString.includes('://')) {
        return `https://${urlString}`;
    }
    // get the protocol from the url
    const protocol = urlString.split('://')[0];
    if (protocol === 'https') {
        return urlString;
    }
    if (protocol === 'http') {
        return urlString.replace('http://', 'https://');
    }
    return urlString.replace(`${protocol}://`, 'https://');
}
export function getParameterByName(name, url = window.location.href) {
    let search = url;
    try {
        search = new URL(url).search;
    }
    catch (e) {
        //
    }
    return new URLSearchParams(search).get(name);
}
export function truncate(value, length, append = '\u2026') {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trunc(length, append);
}
export function stripHtml(content, allowedTags = []) {
    return DOMPurify.sanitize(content, {
        USE_PROFILES: { html: false },
        ADD_TAGS: allowedTags,
    });
}
export function isValidURL(hostname) {
    const regExp = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g;
    return hostname.match(regExp) !== null;
}
